import type { EditorContext, EditorCore } from "@loumy/contracts";
import { useState } from "react";
import { Button, cx, Icon, Spinner, TextInput } from "../ui";
import { MenuBar, type Menu, type MenuEntry } from "./MenuBar";
import { mergeMenus } from "./menuMerge";
import {
  createCliProjectSource,
  createFileSystemAccessSource,
  isValidDocumentName,
  PROJECT_SOURCE_OPTIONS,
} from "./project";
import { useProjectHost } from "./useProjectHost";
import { useProjectSync } from "./useProjectSync";

// §11.1/D87 — GETEILTE Menü-Leiste unter den Editor-Tabs (eine Leiste, Blender/UE-Stil). Sie löst die
// frühere breite „DocumentBar" (D72) ab: Projekt-Verwaltung wandert ins *Datei*-Menü (File→Open Project)
// und der Level-Wechsel in ein *Level*-Menü (Multi-Doc-Host) — der aktuelle Stand (Projekt · Level) steht
// rechts als schlanke Status-Anzeige. Die editor-spezifischen Menüs (Bearbeiten/Ansicht …) liefert der
// aktive Editor über `WebEditorUI.menus`; diese Leiste merged sie per Label mit den Shell-Menüs. Damit
// bleibt „Menüs editor-spezifisch, Auswahl/Projekt Shell" (D66) gewahrt, ohne zwei verstreute Menüleisten.

const msg = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export function EditorMenuStrip({ core, ctx, editorMenus }: { core: EditorCore; ctx: EditorContext; editorMenus: Menu[] }) {
  const host = useProjectHost(core);
  useProjectSync(core, host); // D78 — bidirektionaler Live-Sync (Auto-Save + Watch/Polling), einmal gemountet
  const docSet = core.documentSet;
  const noun = docSet?.noun ?? "Dokument";

  const [connectOpen, setConnectOpen] = useState(false);
  const [connErr, setConnErr] = useState<string | null>(null);

  const openFs = async () => {
    setConnErr(null);
    await host.connect(createFileSystemAccessSource());
  };

  // ── Namens-Prompts (Neu/Umbenennen) — wie zuvor in der DocumentBar, fail-closed gegen ungültige Namen ──
  const promptName = (title: string, initial = ""): string | null => {
    const name = typeof window !== "undefined" ? window.prompt(title, initial) : null;
    if (name == null) return null;
    const trimmed = name.trim();
    if (!isValidDocumentName(trimmed)) {
      setConnErr(`Ungültiger Name „${trimmed}". Erlaubt: Buchstaben/Ziffern/Leerzeichen/-/_.`);
      return null;
    }
    setConnErr(null);
    return trimmed;
  };
  const onNew = async () => {
    const name = promptName(`Neues ${noun} — Name:`);
    if (name) await host.newDocument(name);
  };
  const onRename = async () => {
    if (!host.activeDocument) return;
    const to = promptName(`${noun} umbenennen:`, host.activeDocument);
    if (to && to !== host.activeDocument) await host.renameDocument(host.activeDocument, to);
  };
  const onDelete = async () => {
    if (!host.activeDocument) return;
    if (typeof window !== "undefined" && !window.confirm(`„${host.activeDocument}" wirklich löschen?`)) return;
    await host.deleteDocument(host.activeDocument);
  };

  // ── Shell-„Datei": Projekt-Quellen (registry-getrieben, §1.2/D69) + Schließen. Nur bei Multi-Doc-Editoren. ──
  const fileMenu: Menu[] = docSet
    ? [
        {
          label: "Datei",
          entries: [
            ...PROJECT_SOURCE_OPTIONS.map((opt): MenuEntry => {
              const supported = opt.isSupported();
              return {
                label: opt.requiresConnection ? `${opt.label} verbinden …` : `${opt.label} öffnen …`,
                disabled: !supported || host.busy,
                title: supported ? opt.description : `${opt.label} wird hier nicht unterstützt.`,
                onClick: () => (opt.requiresConnection ? setConnectOpen(true) : void openFs()),
              };
            }),
            { separator: true },
            { label: "Projekt schließen", disabled: !host.source || host.busy, onClick: host.closeProject },
          ],
        },
      ]
    : [];

  // ── Shell-„Level" (= documentSet.noun): Neu/Wechseln/Speichern/Umbenennen/Löschen (Multi-Doc-Host, D72). ──
  const levelMenu: Menu[] = docSet
    ? [
        {
          label: noun,
          entries: [
            { label: `Neues ${noun} …`, disabled: !host.canWrite || host.busy, onClick: () => void onNew() },
            { separator: true },
            ...(!host.source
              ? [{ label: `Erst ein Projekt öffnen (Datei)`, disabled: true } satisfies MenuEntry]
              : host.documents.length === 0
                ? [{ label: `Kein ${noun} vorhanden`, disabled: true } satisfies MenuEntry]
                : host.documents.map(
                    (d): MenuEntry => ({
                      label: d,
                      checked: d === host.activeDocument,
                      disabled: host.busy,
                      title: `Zu „${d}" wechseln`,
                      onClick: () => void host.openDocument(d),
                    }),
                  )),
            { separator: true },
            {
              label: `${noun} speichern`,
              disabled: !host.canWrite || !host.dirty || !host.activeDocument || host.busy,
              title: host.dirty ? "Ungespeicherte Änderungen" : "Gespeichert",
              onClick: () => void host.saveDocument(),
            },
            {
              label: `${noun} umbenennen …`,
              disabled: !host.canMutate || !host.activeDocument || host.busy,
              onClick: () => void onRename(),
            },
            {
              label: `${noun} löschen`,
              disabled: !host.canMutate || !host.activeDocument || host.busy,
              onClick: () => void onDelete(),
            },
          ],
        },
      ]
    : [];

  // Reihenfolge: Datei (Shell) → editor-Menüs (Bearbeiten/Ansicht/Editor-„Datei" wie Export) → Level (Shell).
  // Die Argument-Reihenfolge PINNT die Position (mergeMenus nimmt die erste Erwähnung): Shell-„Datei" steht
  // immer vorn, Shell-Level am Ende — auch wenn ein Editor zufällig gleichnamige Menüs liefert (Codex-P2).
  const menus = mergeMenus(fileMenu, editorMenus, levelMenu);
  // Host-Aktionsfehler (Save/Open/Connect) priorisieren — ein frischer Fehler darf nicht von einem veralteten
  // lokalen Namens-Validierungsfehler (`connErr`) verdeckt werden (Codex-P2).
  const err = host.error ?? connErr;

  return (
    <div className="shrink-0 border-b border-border bg-surface">
      <div className="flex items-center gap-2 px-2 py-1">
        <MenuBar menus={menus} />
        {host.busy && <Spinner size={13} />}

        {/* Status (rechts): aktuelles Projekt · Level — reine Anzeige (Wechsel via Datei-/Level-Menü). */}
        {docSet && (
          <div className="ml-auto flex min-w-0 items-center gap-2 text-[11px]">
            {host.source ? (
              <>
                <span className="inline-flex min-w-0 items-center gap-1.5 rounded-md bg-surface-2 px-2 py-1 text-muted" title="Geöffnetes Projekt">
                  <Icon name="folder-open" size={12} />
                  <span className="max-w-[180px] truncate text-text">{host.projectName}</span>
                </span>
                <span className="inline-flex min-w-0 items-center gap-1.5 rounded-md bg-surface-2 px-2 py-1 text-muted" title={`Aktives ${noun}${host.dirty ? " — ungespeicherte Änderungen" : ""}`}>
                  <Icon name="assets" size={12} />
                  <span className="max-w-[180px] truncate text-text">{host.activeDocument ?? `Kein ${noun}`}</span>
                  {host.dirty && <span className="ml-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-label="ungespeichert" />}
                </span>
              </>
            ) : (
              <span className="rounded-md px-2 py-1 text-muted/70" title="Über Datei → Projekt öffnen ein lokales Projekt verbinden">
                Kein Projekt — <span className="text-muted">Datei → öffnen</span>
              </span>
            )}
          </div>
        )}
      </div>

      {err && <p className="border-t border-danger/30 bg-danger/10 px-3 py-1.5 text-[11px] text-danger">{err}</p>}

      {connectOpen && <ProjectConnectDialog busy={host.busy} onCancel={() => setConnectOpen(false)} onConnect={async (url, token) => {
        setConnErr(null);
        let src;
        try {
          // Konstruktor validiert Base-URL/Token (localhost-only) — Wurf vor jedem Netzwerkzugriff.
          src = createCliProjectSource({ baseUrl: url, token });
        } catch (e) {
          setConnErr(msg(e));
          return false;
        }
        const ok = await host.connect(src);
        if (ok) setConnectOpen(false);
        return ok;
      }} />}
    </div>
  );
}

// CLI-Verbindungsdialog (Token NUR im Speicher, nie persistiert, nie in der URL; localhost-only, D69).
function ProjectConnectDialog({
  busy,
  onConnect,
  onCancel,
}: {
  busy: boolean;
  onConnect: (url: string, token: string) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [cliUrl, setCliUrl] = useState("http://127.0.0.1:4317");
  const [cliToken, setCliToken] = useState("");
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4" onPointerDown={onCancel}>
      <div
        className="w-full max-w-md space-y-3 rounded-xl2 border border-border bg-surface p-4 shadow-panel"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <Icon name="api" size={16} />
          <h2 className="text-sm font-medium text-text">CLI-Projekt verbinden</h2>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-muted">localhost-URL</span>
          <TextInput value={cliUrl} onChange={(e) => setCliUrl(e.target.value)} placeholder="http://127.0.0.1:4317" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-muted">Session-Token (aus der `loumy serve`-Konsole)</span>
          <TextInput type="password" value={cliToken} onChange={(e) => setCliToken(e.target.value)} placeholder="ephemeres Token" />
        </label>
        <p className="text-[11px] leading-relaxed text-muted/80">
          Das Token wird nur im Arbeitsspeicher gehalten (nicht gespeichert, nicht in der URL) und nur an localhost gesendet (D69).
        </p>
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
            Abbrechen
          </Button>
          <Button size="sm" variant="primary" icon="api" disabled={busy} onClick={() => void onConnect(cliUrl, cliToken)}>
            Verbinden
          </Button>
          {busy && <Spinner size={14} />}
        </div>
      </div>
    </div>
  );
}
